import { useCallback, useState } from "react";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/integrations";
import { submissionsApi } from "@/network/network.jsx";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import type { Submission } from "@/bryntum-lib/models/Submission";
import type { ConvertLeadPreview, ConvertLeadResult } from "../api/convertLead.types";

function ensureTaskInStore(
  project: ReturnType<typeof useSwivuBryntumContext>["project"],
  result: ConvertLeadResult,
): boolean {
  if (!project || result.task_id == null) return false;

  const store = project.getCrudStore(STORE_KEYS.taskStore);
  if (!store) return false;

  if (store.getById(result.task_id)) return true;

  if (result.task) {
    store.add(result.task);
    return Boolean(store.getById(result.task_id));
  }

  return false;
}

function markSubmissionConverted(submission: Submission, result: ConvertLeadResult) {
  submission.set({
    taskId: result.task_id,
    convertedAt: submission.convertedAt ?? new Date(),
    ...(result.location_id != null ? { locationId: result.location_id } : {}),
  });
}

export function useConvertLead() {
  const { project, selectedBoard, entityManager } = useSwivuBryntumContext();
  const boardSlug = (selectedBoard?.record as { slug?: string } | undefined)?.slug;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState<ConvertLeadPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const openTask = useCallback(
    (taskId: number) => {
      if (!entityManager || taskId == null) return;
      entityManager.openEntityManager({
        entityType: ENTITY_TYPES.TASK,
        entityId: taskId,
      });
    },
    [entityManager],
  );

  const openConvertedTask = useCallback(
    (taskId: number | null | undefined, result?: ConvertLeadResult) => {
      if (taskId == null) {
        toast.error("The task could not be found.");
        return;
      }

      if (result) {
        ensureTaskInStore(project, result);
      }

      const store = project?.getCrudStore(STORE_KEYS.taskStore);
      if (!store?.getById(taskId)) {
        toast.error(
          "The task was created, but it is not available to open yet. Refresh and try again.",
        );
        return;
      }

      openTask(taskId);
    },
    [openTask, project],
  );

  const closePreview = useCallback(() => {
    if (confirming) return;
    setPreviewOpen(false);
    setPreview(null);
  }, [confirming]);

  const startConvert = useCallback(
    async (submission: Submission) => {
      if (!boardSlug) {
        toast.error("Board is unavailable.");
        return;
      }

      if (submission.isConverted && submission.taskId != null) {
        openConvertedTask(submission.taskId);
        return;
      }

      setPreviewOpen(true);
      setPreview(null);
      setPreviewLoading(true);

      try {
        const res = await submissionsApi(boardSlug).convertPreview(submission.id);
        const data = res.data as ConvertLeadPreview;

        if (data.already_converted) {
          const taskId = data.existing_task_id ?? submission.taskId;
          setPreviewOpen(false);
          setPreview(null);
          if (taskId != null) {
            submission.set({
              taskId,
              convertedAt: submission.convertedAt ?? new Date(),
            });
            toast.message("This lead was already converted.");
            openConvertedTask(taskId);
          } else {
            toast.error("This lead is already converted, but the task could not be found.");
          }
          return;
        }

        setPreview(data);
      } catch (err) {
        toast.error(getApiErrorMessage(err, "Unable to prepare convert preview."));
        setPreviewOpen(false);
      } finally {
        setPreviewLoading(false);
      }
    },
    [boardSlug, openConvertedTask],
  );

  const confirmConvert = useCallback(
    async (submission: Submission) => {
      if (!boardSlug || confirming) return;

      setConfirming(true);
      try {
        const res = await submissionsApi(boardSlug).convertToTask(submission.id);
        const data = res.data as ConvertLeadResult;

        markSubmissionConverted(submission, data);

        setPreviewOpen(false);
        setPreview(null);

        if (data.already_converted) {
          toast.message("This lead was already converted.");
        } else {
          toast.success("Lead converted to task");
        }

        openConvertedTask(data.task_id, data);
      } catch (err) {
        toast.error(getApiErrorMessage(err, "Unable to convert lead to task."));
      } finally {
        setConfirming(false);
      }
    },
    [boardSlug, confirming, openConvertedTask],
  );

  return {
    previewOpen,
    preview,
    previewLoading,
    confirming,
    startConvert,
    confirmConvert,
    openTask,
    setPreviewOpen: (open: boolean) => {
      if (!open) closePreview();
      else setPreviewOpen(true);
    },
  };
}
